// Pattern lifecycle check-ins — "is this still part of your story?"

import { apiGet, apiPost } from './client';
import { openCipher } from './cipher';
import { subjectPhrase } from './labels';

export interface PatternCheckIn {
  id: string;
  canonicalId: string;
  reason: string;
  question: string;
  previousStatus: string | null;
  triggeredAt: string;
}

/**
 * The three answers that fit almost every check-in, and the three that occasionally do.
 *
 * Six equal chips made every card a small form; Hick's law says the time to choose grows with the
 * options, and six of them repeated down a column is why the stack read as a questionnaire. These
 * three carry the common cases and the rest wait behind "something else".
 */
export const CHECK_IN_PRIMARY = [
  { id: 'still_present', label: 'Still here' },
  { id: 'improving', label: 'Easing' },
  { id: 'resolved', label: 'Resolved' },
] as const;

export const CHECK_IN_MORE = [
  { id: 'comes_and_goes', label: 'It comes and goes' },
  { id: 'context_specific', label: 'Only in certain situations' },
  { id: 'never_fit', label: 'This never fit me' },
] as const;

export const CHECK_IN_RESPONSES = [...CHECK_IN_PRIMARY, ...CHECK_IN_MORE];

interface CheckInRow {
  id: string;
  canonical_id: string;
  check_in_reason?: string | null;
  question_cipher?: string | null;
  previous_status?: string | null;
  triggered_at?: string | null;
}

export interface CheckInQueue {
  /** What Oreia is asking now — already paced by the server, not the whole backlog. */
  checkIns: PatternCheckIn[];
  /** Everything still unanswered, so the UI can say that the rest are waiting, not lost. */
  totalOpen: number;
}

/** The slot the worker leaves where the pattern's name goes. See lifecycle.ts's SUBJECT_SLOT. */
const SUBJECT_SLOT = '{subject}';

/**
 * Put the pattern's name into the question the worker sealed.
 *
 * The worker knows the evidence and writes the sentence; it does not know that
 * `theme:inner_critic` is called "the inner critic", because that vocabulary lives here alongside
 * every other place a pattern is named. Filling the slot at read time is what keeps a check-in
 * calling something by the same name the rest of the app calls it.
 *
 * "this" is the fallback, and it is the right one: it is the word these questions used before there
 * was a slot, so an older sealed row or an id with no written name reads exactly as it always did
 * rather than as a bug someone can see.
 */
export function fillSubject(question: string, canonicalId: string): string {
  if (!question.includes(SUBJECT_SLOT)) return question;
  return question.split(SUBJECT_SLOT).join(subjectPhrase(canonicalId) || 'this');
}

export async function fetchCheckIns(dek: CryptoKey, signal?: AbortSignal): Promise<CheckInQueue> {
  const res = await apiGet<{ check_ins: CheckInRow[]; total_open?: number }>('/api/check-ins', signal);
  const rows = await Promise.all(
    (res.check_ins ?? []).map(async (row) => ({
      id: row.id,
      canonicalId: row.canonical_id,
      reason: row.check_in_reason ?? '',
      question: fillSubject(await openCipher(dek, row.question_cipher), row.canonical_id),
      previousStatus: row.previous_status ?? null,
      triggeredAt: row.triggered_at ?? '',
    })),
  );
  const checkIns = rows.filter((r) => r.question.trim().length > 0 || r.canonicalId.length > 0);
  return { checkIns, totalOpen: res.total_open ?? checkIns.length };
}

export async function respondToCheckIn(id: string, response: string): Promise<void> {
  await apiPost('/api/check-ins/respond', { id, response });
}

export { patternLabel } from './labels';
